// /pages/BinDataSummaryPage.js

import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import DataGrid, {
  Column, FilterRow, HeaderFilter, SearchPanel, Sorting, Export, Paging, ColumnChooser, Summary, TotalItem
} from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import SelectBox from "devextreme-react/select-box";
import notify from 'devextreme/ui/notify';
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

const SUMMARY_API_URL = `${API_URL}/api/oracle/bin-data-summary`;
const CUST_API_URL = `${API_URL}/api/local/common/custs`;
const DEVICE_API_URL = `${API_URL}/api/local/common/devices`;

// 데이터를 피벗팅하는 함수 (변경 없음)
const processPivotData = (rawData) => {
  if (!rawData || rawData.length === 0) {
    return { pivotedData: [], dynamicColumns: [] };
  }
  const groupedData = new Map();
  const dateSet = new Set();

  rawData.forEach(row => {
    const key = row.MAT_ID;
    if (!groupedData.has(key)) {
      // YLD_TOTAL 값은 MAT_ID 그룹 내에서 동일하므로, 첫 번째 행의 값을 사용합니다.
      groupedData.set(key, { MAT_ID: row.MAT_ID, YLD_TOTAL: row.YLD_TOTAL, Dates: {} });
    }
    const entry = groupedData.get(key);
    if (row.BASIC_DATE && row.YLD !== null) {
      entry.Dates[row.BASIC_DATE] = row.YLD;
      dateSet.add(row.BASIC_DATE);
    }
  });

  // 주차(WW) 또는 월(MM) 순서에 맞게 정렬합니다.
  const dynamicColumns = Array.from(dateSet).sort((a, b) => {
    // 'YYYY WW' 또는 'YYYY/MM' 형식을 가정
    const numA = parseInt(a.split(' ')[1] || a.split('/')[1], 10);
    const numB = parseInt(b.split(' ')[1] || b.split('/')[1], 10);
    return numA - numB;
  });

  const pivotedData = Array.from(groupedData.values()).map(item => {
    const finalRow = { ...item };
    dynamicColumns.forEach(dateCol => {
      finalRow[dateCol] = item.Dates[dateCol] || null;
    });
    delete finalRow.Dates;
    return finalRow;
  });

  return { pivotedData, dynamicColumns };
};


export default function BinDataSummaryPage({ user }) {
  const [data, setData] = useState([]);
  const [dynamicDateColumns, setDynamicDateColumns] = useState([]);
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  
  const [selectedFactory, setSelectedFactory] = useState('FT01');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedGubun, setSelectedGubun] = useState('MM');
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState('ALL');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const dataGridRef = useRef(null);

  const yearList = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString());
  const gubunList = [{ code: 'MM', name: '월별' }, { code: 'WW', name: '주별' }];

  // useEffect 훅 (CUST, DEVICE 목록 조회)
  useEffect(() => {
    if (!selectedFactory) return;
    axios.get(CUST_API_URL, { params: { factory: selectedFactory } }).then(res => setCustList(res.data.data || []));
  }, [selectedFactory]);

  useEffect(() => {
    if (!selectedFactory || !selectedCust) { setDeviceList([]); setSelectedDevice('ALL'); return; }
    axios.get(DEVICE_API_URL, { params: { factory: selectedFactory, custCd: selectedCust } })
      .then(res => {
        const fetchedDevices = res.data.data || [];
        setDeviceList([{ CODE_CD: 'ALL' }, ...fetchedDevices]);
        setSelectedDevice('ALL');
      });
  }, [selectedFactory, selectedCust]);

  useEffect(() => {
    if (user?.role === "CUST" && custList.length > 0) {
      const found = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      if (found) setSelectedCust(found.CODE_CD);
    }
  }, [user, custList]);

  const fetchData = useCallback(() => {
    if (!selectedFactory || !selectedCust) {
      notify("Factory와 CUST는 필수 선택 항목입니다.", "warning", 2000);
      return;
    }
    setLoading(true);
    setError("");
    setData([]);
    setDynamicDateColumns([]);

    const params = {
      factory: selectedFactory,
      year: selectedYear,
      gubun: selectedGubun,
      custCd: selectedCust,
      device: selectedDevice,
    };
    
    axios.get(SUMMARY_API_URL, { params })
      .then(res => {
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        const { pivotedData, dynamicColumns } = processPivotData(res.data.data);
        setData(pivotedData.map((row, idx) => ({ ...row, id: idx })));
        setDynamicDateColumns(dynamicColumns);
      })
      .catch(e => {
        const errorMessage = e.response?.data?.retMsg || e.message;
        setError(`조회 오류: ${errorMessage}`);
        notify(errorMessage, 'error', 3000);
      })
      .finally(() => setLoading(false));
  }, [selectedFactory, selectedYear, selectedGubun, selectedCust, selectedDevice]);
  
  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Bin_Data_Summary");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true })
      .then(() => workbook.xlsx.writeBuffer())
      .then(buffer => saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Bin_Data_Summary.xlsx"));
    e.cancel = true;
  };

  return (
    <div style={{ padding: 20, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>Bin Data Summary</h3>
        <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center' }}>
          <SelectBox items={['FT01', 'PT01']} value={selectedFactory} onValueChanged={e => setSelectedFactory(e.value)} label="FACTORY" width={150} />
          <SelectBox items={yearList} value={selectedYear} onValueChanged={e => setSelectedYear(e.value)} label="YEAR" width={120} />
          <SelectBox items={gubunList} value={selectedGubun} valueExpr="code" displayExpr="name" onValueChanged={e => setSelectedGubun(e.value)} label="구분" width={120} />
          <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_NM" onValueChanged={e => setSelectedCust(e.value)} disabled={user?.role === "CUST"} searchEnabled showClearButton={user?.role !== "CUST"} placeholder="Select CUST" label="CUST" width={220} />
          <SelectBox dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} searchEnabled label="DEVICE" width={220} />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "#d9534f", fontWeight: 'bold', marginTop: '10px' }}>{error}</p>}
      </div>
      
      {/* ★★★ [수정] 그리드 영역 (전체 기능 복원) ★★★ */}
      <div style={{ flex: 1, position: 'relative' }}>
        {loading && <div className="loading-overlay"></div>}
        <DataGrid
          ref={dataGridRef}
          dataSource={data}
          keyExpr="MAT_ID"
          onExporting={onExporting}
          height="100%"
          showBorders={true}
          allowColumnResizing={true}
          columnAutoWidth={true}
          hoverStateEnabled={true}
        >
          <Export enabled={true} />
          <Paging defaultPageSize={20} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} width={240} placeholder="Search..." />
          <Sorting mode="multiple" />
          <ColumnChooser enabled={true} />

          {/* 고정 컬럼 */}
          <Column dataField="MAT_ID" caption="Device" width={200} fixed={true} />
          
          {/* 동적 날짜(주차/월) 컬럼 */}
          {dynamicDateColumns.map(dateCol => (
            <Column
              key={dateCol}
              dataField={dateCol}
              caption={dateCol.replace(selectedYear, '').trim().replace('/', '-')} // 'YYYY WW' -> 'WW', 'YYYY/MM' -> '-MM'
              dataType="number"
              format={{ type: 'fixedPoint', precision: 2 }}
              alignment="right"
              width={100}
              cellRender={({ value }) => value ? `${value}%` : ''} // % 기호 추가
            />
          ))}
          
          {/* 고정 합계 컬럼 */}
          <Column 
            dataField="YLD_TOTAL" 
            caption="Total Yield" 
            dataType="number" 
            format={{ type: 'fixedPoint', precision: 2 }} 
            alignment="right" 
            width={120} 
            cellRender={({ value }) => value ? `${value}%` : ''} // % 기호 추가
          />

          {/* 하단 요약은 이 그리드에서는 의미가 적어 제외했습니다. 필요 시 추가 가능합니다. */}
        </DataGrid>
      </div>
    </div>
  );
}